import moment from "moment";
import "moment/locale/es";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight.vue";
import { salesAnalysisModalConfig } from "@/components/financialReports/salesAnalysis/salesDrawerModals";
import HeaderCostsAnalysis from "@/components/financialReports/costsAnalysis/HeaderCostsAnalysis.vue";
import BodyCostsAnalysis from "@/components/financialReports/costsAnalysis/BodyCostsAnalysis.vue";
import HeaderExpensesAnalysis from "@/components/financialReports/expensesAnalysis/HeaderExpensesAnalysis.vue";
import BodyExpensesAnalysis from "@/components/financialReports/expensesAnalysis/BodyExpensesAnalysis.vue";

// Drawers del mes que el Dashboard abre IN SITU al clickear una fila (Steve,
// 2026-10-06: «mostrar solo el modal, sin cambiar de tab»). Son los mismos
// drawers de Ventas, Costos y Gastos, con el mismo módulo Vuex y la misma
// config de modal que usa cada tab al consumir su `pendingDrill`; acá sólo se
// describe qué pedir y qué abrir por tab. Las filas cuyo tab no tiene drawer
// mensual (márgenes, EBITDA, No Operacional) no son clickeables (D3).
//
// `account`: una fila de CUENTA abre el drawer acotado a esa cuenta — una
// línea (nivel 2 del plan) o una cuenta de nivel 3 bajo la línea, que viaja
// con la línea como `accountGroup` para desambiguar homónimas, igual que el
// drill del Resumen; un total abre el análisis general. Ventas siempre abre
// el general, como hace su tab al clickear cualquier celda.

// "Ago 2026": sin el punto que moment pone en las abreviaturas en español.
const periodLabelOf = (month) => moment(month, "YYYY-MM")
	.locale("es")
	.format("MMM YYYY")
	.replace(".", "")
	.replace(/^./, (c) => c.toUpperCase());

function breakdownModalConfig({ headerComp, bodyComp, theme }) {
	return ({ periodLabel, endMonth, getNow }) => ({
		hasModalCard: true,
		customClass: "wide-summary-modal",
		canCancel: ["escape", "outside"],
		component: ModalFullHeightRight,
		props: {
			headerComp,
			bodyComp,
			headerProps: { periodLabel, endMonth, getNow },
			bodyProps: null,
			theme,
		},
	});
}

export const MONTH_DRAWERS = Object.freeze({
	ventas: {
		analysisNs: "salesAnalysis",
		fetchAction: "fetchSalesAnalysis",
		// El modal de Ventas agrupa su distribución por las dos primeras
		// categorías, como el tab (`analysisGroupByForOpen`).
		fetchPayload: ({ month, categories }) => ({
			month,
			account: null,
			groupBy: Array.isArray(categories) ? categories.slice(0, 2) : [],
			selectedItems: {},
		}),
		modalConfig: salesAnalysisModalConfig,
	},
	costos: {
		analysisNs: "costsAnalysis",
		fetchAction: "fetchCostsAnalysis",
		fetchPayload: ({ month, row }) => breakdownPayload(month, row),
		modalConfig: breakdownModalConfig({
			headerComp: HeaderCostsAnalysis, bodyComp: BodyCostsAnalysis, theme: "costos",
		}),
	},
	gastos: {
		analysisNs: "expensesAnalysis",
		fetchAction: "fetchExpensesAnalysis",
		fetchPayload: ({ month, row }) => breakdownPayload(month, row),
		modalConfig: breakdownModalConfig({
			headerComp: HeaderExpensesAnalysis, bodyComp: BodyExpensesAnalysis, theme: "gastos",
		}),
	},
});

// Tabs cuya fila abre un drawer desde el Dashboard.
export const DRAWER_TABS = Object.freeze(Object.keys(MONTH_DRAWERS));

function breakdownPayload(month, row) {
	const account = row && row.account ? row.account : null;
	return {
		month,
		account,
		accountLevel: account ? String(row.accountLevel || 2) : null,
		accountGroup: (account && row.accountGroup) || null,
		groupBy: null,
		selectedItems: {},
	};
}

/**
 * Lo que hace falta para abrir el drawer de una fila: a quién pedirle los
 * datos, con qué payload, y la config del modal. `null` si la fila no tiene
 * drawer mensual.
 */
export function drawerFor(row, { month, categories, getNow }) {
	const drawer = row && MONTH_DRAWERS[row.targetTab];
	if (!drawer) return null;
	const payloadFor = (monthKey) => drawer.fetchPayload({ month: monthKey, row, categories });
	return {
		analysisNs: drawer.analysisNs,
		fetchAction: drawer.fetchAction,
		payloadFor,
		modalConfig: drawer.modalConfig({
			periodLabel: periodLabelOf(month),
			endMonth: moment(month, "YYYY-MM").toDate(),
			getNow,
		}),
	};
}
